import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Platform } from 'react-native';

// Inject floating animation CSS once
const injectFloatingAnimation = () => {
  if (typeof document !== 'undefined' && !document.getElementById('tournament-marker-animation')) {
    const style = document.createElement('style');
    style.id = 'tournament-marker-animation';
    style.textContent = `
      @keyframes tournamentMarkerFloat {
        0%, 100% {
          transform: translateY(0px);
        }
        50% {
          transform: translateY(-8px);
        }
      }
      @keyframes tournamentGlow {
        0%, 100% {
          box-shadow: 0 0 10px rgba(255, 152, 0, 0.5);
        }
        50% {
          box-shadow: 0 0 20px rgba(255, 152, 0, 0.8);
        }
      }
      @keyframes tournamentShadowFadeIn {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
      @keyframes tournamentShadowPulse {
        0%, 100% {
          transform: translateX(-50%) scale(1);
          opacity: 0.4;
        }
        50% {
          transform: translateX(-50%) scale(0.6);
          opacity: 0.2;
        }
      }
    `;
    document.head.appendChild(style);
  }
};

// TournamentMarker - shows tournaments on the map with participant count
const TournamentMarker = ({ tournament, onClick, participantCount = 0, showShadowAnimation = true }) => {
  if (Platform.OS === 'web') {
    useEffect(() => {
      injectFloatingAnimation();
    }, []);

    const animationDelay = useRef(Math.random() * 2).current;

    // Determine tournament state for color
    const isActive = tournament.status === 'IN_PROGRESS';
    const isRegistrationOpen = tournament.status === 'REGISTRATION';
    const markerColor = isActive ? '#4CAF50' : isRegistrationOpen ? '#FF9800' : '#9E9E9E';

    return (
      <div
        onClick={onClick}
        style={{
          position: 'relative',
          width: '70px',
          height: '95px',
          cursor: 'pointer',
          transformOrigin: 'bottom center',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        {/* Floating pin container */}
        <div
          style={{
            animation: `tournamentMarkerFloat 2.5s ease-in-out infinite`,
            animationDelay: `${animationDelay}s`,
          }}
        >
          {/* Pin SVG with trophy icon */}
          <svg 
            width="70" 
            height="95" 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 44 60" 
            style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
            }}
          >
            {/* Outer glow for active tournaments */}
            {isActive && (
              <path 
                d="M 22 -3 C 11 -3 3 5 3 15 C 3 22 7 28 22 55 C 37 28 41 22 41 15 C 41 5 33 -3 22 -3 Z" 
                fill="none"
                stroke="#4CAF50"
                strokeWidth="2"
                opacity="0.5"
                style={{
                  animation: 'tournamentGlow 2s ease-in-out infinite',
                }}
              />
            )}
            {/* Main pin shape */}
            <path 
              d="M 22 0 C 13 0 6 7 6 16 C 6 22 9 27 22 52 C 35 27 38 22 38 16 C 38 7 31 0 22 0 Z" 
              fill={markerColor}
              stroke="none"
            />
            {/* White circle for icon background */}
            <circle cx="22" cy="16" r="12" fill="white" fillOpacity="0.95"/>
          </svg>
          
          {/* Trophy Icon */}
          <div
            style={{
              position: 'absolute',
              top: '14px',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '22px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            🏆
          </div>

          {/* Participant count badge */}
          <div
            style={{
              position: 'absolute',
              top: '-5px',
              right: '5px',
              minWidth: '22px',
              height: '22px',
              borderRadius: '11px',
              backgroundColor: '#E53935',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 'bold',
              color: 'white',
              padding: '0 5px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              border: '2px solid white',
            }}
          >
            {participantCount}
          </div>

          {/* Status indicator */}
          <div
            style={{
              position: 'absolute',
              bottom: '28px',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '2px 8px',
              borderRadius: '10px',
              backgroundColor: markerColor,
              fontSize: '9px',
              fontWeight: 'bold',
              color: 'white',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            {isActive ? 'LIVE' : isRegistrationOpen ? 'OPEN' : 'DRAFT'}
          </div>
        </div>
        
        {/* Shadow below marker - only animate at higher zoom to avoid clutter when zoomed out */}
        {showShadowAnimation && (
        <div
          style={{
            position: 'absolute',
            bottom: '-10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '45px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            zIndex: -1,
            animation: `tournamentShadowFadeIn 120ms ease-out forwards, tournamentShadowPulse 2.5s ease-in-out 120ms infinite`,
            animationDelay: `0s, ${animationDelay}s`,
          }}
        />
        )}
      </div>
    );
  }

  // For mobile (React Native), use View components
  return (
    <View style={styles.markerContainer}>
      <View style={[styles.pin, { backgroundColor: tournament.status === 'IN_PROGRESS' ? '#4CAF50' : '#FF9800' }]}>
        <View style={styles.iconContainer}>
          {/* Trophy icon would go here */}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  markerContainer: {
    position: 'relative',
    alignItems: 'center',
  },
  pin: {
    width: 66,
    height: 90,
    borderRadius: 33,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'white',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default TournamentMarker;

